/**
 * LeadRail — the ONE rail on the lead page (CRM UI streamline D-4).
 *
 * WHAT IT REPLACES. The right column was a three-tab ContactRail, and the agent
 * card lived somewhere else entirely — above the conversation on phones, below
 * the rail on laptops — so "what is the agent doing" and "what do I do next"
 * were two different places to look, and the agent's own status scrolled away
 * with whatever was above it. This is one rail with four tabs: Next steps ·
 * Agent · Screening · Profile, with the agent's status line PINNED above the tab
 * strip so it is readable no matter which tab is open. Next steps is the tab
 * that opens by default (owner ask 2026-08-06) — nothing auto-switches the
 * recruiter off it; the Agent tab's own dot is what says something is waiting
 * there.
 *
 * THE AGENT PANEL MOUNTS ALWAYS. It is the sole source of `AgentThreadState`
 * for the whole page — the D-3 attention strip renders its flags, drafts and
 * in-flight sends from it, and the composer draws its coexistence warning from
 * it — so its mount can never be gated on the Agent tab being open. It also
 * reports the presence that decides whether that tab exists at all. When the
 * lead has no agent surface AgentPanel renders null, and the panel box collapses
 * with `empty:hidden` rather than painting an empty card behind a hidden tab.
 *
 * HIDDEN, NEVER UNMOUNTED. All four panels stay mounted and hide with `hidden`
 * (the F1 rule). Switching tabs must not throw away a half-typed task, a
 * prescreen runner mid-question, or a records request with three offices picked
 * — and unmounting a card that owns a POST invites the recruiter to send it
 * twice.
 *
 * NO NEW DATA. The rail is layout: no fetch, no poll, no timer. Every
 * endpoint, id and handler is the one that was already there, passed straight
 * through — including the randomized-conflict banner, which is derived from the
 * `detail.memberships` the page already loaded.
 *
 * ONE CROSS-TAB WARNING. RandomizedConflictBanner sits above the tab strip, not
 * inside the Screening tab's Studies card where it started (L4): "this lead is
 * enrolled in another study" has to reach the recruiter who is texting from Next
 * steps, not only the one who opened Screening.
 *
 * THE PHONE/TABLET SHEET (D-12). Below `lg` this same tree docks to the bottom
 * of the viewport as a swipe-up sheet instead of stacking below a
 * viewport-tall conversation. Collapsed, the peek shows the drag handle and
 * the SAME pinned status-line slot — so state · phase · Take over stay
 * reachable with the sheet closed, extending the D-4 "Take over on every tab"
 * guarantee to phones. Expanded, the tab strip + panels appear and scroll
 * inside the sheet. The mechanism is CSS tiers + `hidden` classes on the
 * strip/panels wrapper: NOTHING re-mounts on expand/collapse or on a
 * breakpoint crossing — AgentPanel keeps its single mount (it is the page's
 * only AgentThreadState source), the status line keeps its single portal
 * target, and the act()/busy gate never sees a second instance. Escape
 * collapses via the kit's popover discipline — a bubble-phase window listener
 * attached only while expanded (capture belongs to ConfirmDialog).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronUp } from 'lucide-react';
import { ContactDetailResponse, Enrollment, ScheduleReadiness, ScheduleReadinessItem } from '../../../types';
import AgentPanel from '../../AgentPanel';
import type { AgentThreadState } from '../../AgentPanel';
import ScreeningTab from '../../prescreen/ScreeningTab';
import ProfileTab from '../../profile/ProfileTab';
import { RandomizedConflictBanner } from '../../StudiesCard';
import { Z, touchTarget } from '../../ui';
import NextStepsTab from './NextStepsTab';
import {
  agentTabAttention, initialSheetExpanded, resolveRailTab, sheetSwipeAction, showAgentTab,
} from './railLogic';
import type { AgentPresence, RailTab } from './railLogic';

export interface LeadRailProps {
  detail: ContactDetailResponse;
  enrollments: Enrollment[];
  reloadToken: number;
  /** Debounced Realtime ping (ContactDetail) — silent rail-card refetches. */
  pingTick?: number;
  onSent: () => void;
  onChanged: () => void;
  isSuperAdmin: boolean;
  reenrolling: boolean;
  onReEnroll: () => void;
  /** Attempt count is unknown until ScreeningTab loads; dot shows pre-attempt nudge. */
  hasRunnableStudy: boolean;
  /** Drop a rendered SMS draft into the docked composer (booking + form links). */
  prefillComposer?: (text: string) => void;
  /** Scheduling readiness for the lead's current study (owned by ContactDetail). */
  scheduleReadiness?: ScheduleReadiness | null;
  /** Tick a manual scheduling gate (CRC / investigator / custom sign-off). */
  onToggleGate: (item: ScheduleReadinessItem, done: boolean) => Promise<void>;
  /** The contact AgentPanel runs against — passed through untouched. */
  agentContactId: string;
  /** The lead's primary/considering study id, when known — enables "Hand to agent". */
  agentStudyId: string | null;
  /** The page's existing thread-state consumer. The rail wiretaps this on the
   *  way past (see the wiretap below) and forwards the SAME object. */
  onThreadState?: (s: AgentThreadState | null) => void;
  /** Escalation deep-link: a screening flag was clicked. Forwarded to
   *  AgentPanel, and it selects the Agent tab so the jump has something to
   *  scroll to. */
  focusScreenKey?: string | null;
  focusNonce?: number;
}

export default function LeadRail({
  detail, enrollments, reloadToken, pingTick = 0, onSent, onChanged,
  isSuperAdmin, reenrolling, onReEnroll, hasRunnableStudy, prefillComposer,
  scheduleReadiness, onToggleGate, agentContactId, agentStudyId, onThreadState,
  focusScreenKey = null, focusNonce = 0,
}: LeadRailProps) {
  const c = detail.contact;

  // Deep-links (escalation popups, readiness jump-links, console hand-offs)
  // land on a specific rail tab via ?tab=; the pre-D-4 values still resolve
  // (C-20) and anything unrecognized falls back to the work surface.
  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const [tab, setTab] = useState<RailTab>(() => resolveRailTab(urlTab) ?? 'next');

  /** Which panels have ever been shown. A panel mounts on its FIRST visit and
   *  then stays mounted for the life of this rail — hidden, never unmounted, so
   *  half-typed state and in-flight requests survive tab switching (the F1
   *  rule). What this avoids is the other extreme: mounting all four on arrival
   *  fired every tab's fetches on every lead open and on every reloadToken
   *  bump, ~8-13 requests to render one visible panel.
   *
   *  Written during render, not in an effect, so the tab being switched TO is
   *  mounted in the same commit that reveals it — an effect would paint one
   *  frame of empty box first. Adding is idempotent and the set only grows, so
   *  a StrictMode double-render is harmless.
   *
   *  AgentPanel is deliberately NOT in here: it mounts eagerly because it is
   *  the page's only source of AgentThreadState, which the attention strip and
   *  the composer read whether or not the Agent tab is showing. */
  const visitedRef = useRef<Set<RailTab>>(new Set());
  visitedRef.current.add(tab);
  const visited = visitedRef.current;

  /** What AgentPanel last reported about this lead's agent surface. */
  const [presence, setPresence] = useState<AgentPresence | null>(null);
  /** The thread state the rail itself needs (the Agent tab's dot). The parent
   *  gets the identical object — see the wiretap. */
  const [localThread, setLocalThread] = useState<AgentThreadState | null>(null);
  /** Where AgentPanel portals its pinned status line. State (not a ref) so the
   *  first render after mount hands the panel a real element to portal into. */
  const [slotEl, setSlotEl] = useState<HTMLDivElement | null>(null);

  /** D-12: below `lg` the rail is a bottom sheet. true ⇢ tabs + panels shown,
   *  false ⇢ peek (handle + status line). Inert at `lg`+ — only max-lg classes
   *  and the while-expanded Escape listener read it. Starts expanded when the
   *  URL names a rail tab: that deep link asked to SEE the tab, and on a phone
   *  "visible" means the sheet is open. */
  const [sheet, setSheet] = useState<boolean>(() => initialSheetExpanded(urlTab));
  /** Swipe bookkeeping for the handle — where the touch started, and whether
   *  the gesture already handled this interaction (suppresses the tap that
   *  fires after touchend). */
  const touchStartY = useRef<number | null>(null);
  const swipeConsumedRef = useRef(false);

  /** The last `?tab=` we acted on, so the effect below reacts to URL CHANGES
   *  without listing `tab` in its deps — which would drag the rail back to the
   *  URL's tab every time the recruiter picked a different one. */
  const lastUrlTabRef = useRef(urlTab);

  // A LATER ?tab= (an in-page deep link — the URL changes, the page does not
  // remount) moves the rail; a non-rail value is left alone rather than
  // bouncing the recruiter to Next steps.
  useEffect(() => {
    if (urlTab === lastUrlTabRef.current) return;
    lastUrlTabRef.current = urlTab;
    const named = resolveRailTab(urlTab);
    if (!named) return;
    setTab(named);
    // An in-page deep link wants its tab SEEN — on phones that means opening
    // the sheet too. Inert at lg+.
    setSheet(true);
  }, [urlTab]);

  // The tab went away underneath us (conversation closed, offer withdrawn):
  // fall back to the work surface rather than showing a panel with no tab.
  useEffect(() => {
    if (tab === 'agent' && !showAgentTab(presence)) setTab('next');
  }, [tab, presence]);

  // Escalation banner / summary-note jump: AgentPanel's own scroll+highlight
  // effect needs its subtree visible, and it fires 80ms later — selecting the
  // tab synchronously in this commit is enough to beat it.
  useEffect(() => {
    if (focusScreenKey) {
      setTab('agent');
      // The panel's scroll+highlight fires 80ms later and needs its subtree
      // visible — on a phone that means the sheet must be open. Same-commit
      // state, so the paint beats the timer.
      setSheet(true);
    }
  }, [focusScreenKey, focusNonce]);

  // Escape collapses the sheet — the kit's popover discipline: a bubble-phase
  // window listener attached only while expanded (the capture phase belongs to
  // ConfirmDialog, which must win when it is stacked above).
  useEffect(() => {
    if (!sheet) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSheet(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheet]);

  const onHandleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    swipeConsumedRef.current = false;
  };
  const onHandleTouchMove = (e: React.TouchEvent) => {
    if (touchStartY.current == null) return;
    const action = sheetSwipeAction(touchStartY.current, e.touches[0].clientY, sheet);
    if (action) {
      setSheet(action === 'expand');
      swipeConsumedRef.current = true;
      touchStartY.current = null;
    }
  };
  const onHandleTouchEnd = () => { touchStartY.current = null; };
  const onHandleClick = () => {
    // A swipe already acted on this touch; the click that follows touchend
    // must not immediately toggle the sheet back.
    if (swipeConsumedRef.current) { swipeConsumedRef.current = false; return; }
    setSheet((s) => !s);
  };

  // Wiretap: the rail reads the thread state (for the Agent tab's dot) and
  // hands the parent the EXACT object AgentPanel reported — same reference, no
  // filtering, no reshaping, no deferral. The attention strip and the composer
  // downstream of it must see precisely what the panel saw.
  const onThreadStateRef = useRef(onThreadState);
  onThreadStateRef.current = onThreadState;
  const onThreadStateWiretap = useCallback((s: AgentThreadState | null) => {
    setLocalThread(s);
    onThreadStateRef.current?.(s);
  }, []);

  const contactStudySlugs = useMemo(
    () => [...new Set((detail.leads || []).map((l) => l.study_slug).filter(Boolean))] as string[],
    [detail.leads]
  );

  const profileLabel = detail.profile_completeness
    ? `Profile · ${detail.profile_completeness.percent}%`
    : 'Profile';

  const agentTab = showAgentTab(presence);

  const tabBtn = (key: RailTab, label: string, dot?: boolean) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      className={`relative flex-1 rounded-lg px-1.5 py-2 text-sm font-medium transition-colors duration-150 ${touchTarget} sm:px-2 ${
        tab === key
          ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-100'
          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
      }`}
    >
      {label}
      {dot && tab !== key && (
        <>
          <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-primary-500" aria-hidden="true" />
          <span className="sr-only">— needs attention</span>
        </>
      )}
    </button>
  );

  /* At lg+ the parent wrapper owns the laptop height and this is the plain
     rail column. Below lg the SAME tree docks to the viewport bottom as the
     sheet; expanded/collapsed only ever toggles classes — nothing re-mounts. */
  return (
    <>
      {/* Backdrop behind the expanded sheet (phones/tablets); tap collapses.
          Overlay-tier: the sheet itself sits one tier up, and the corner alert
          cards (same tier, mounted later at the app root) still paint above
          this dimmer — an incoming hot-lead alert stays visible. */}
      <div
        className={`fixed inset-0 bg-black/50 lg:hidden ${Z.overlay} ${sheet ? '' : 'hidden'}`}
        onClick={() => setSheet(false)}
        aria-hidden="true"
      />
      <div
        className={`flex min-h-0 flex-col gap-2 lg:h-full max-lg:fixed max-lg:inset-x-0 max-lg:bottom-0 ${Z.modal} max-lg:rounded-t-2xl max-lg:border-t max-lg:border-gray-200 max-lg:bg-white max-lg:px-3 max-lg:pb-[env(safe-area-inset-bottom)] max-lg:shadow-[0_-8px_30px_-12px_rgba(0,0,0,0.35)] dark:max-lg:border-gray-700 dark:max-lg:bg-gray-800 ${
          sheet ? 'max-lg:max-h-[85vh] supports-[height:1dvh]:max-lg:max-h-[85dvh]' : ''
        }`}
      >
        {/* 0 — the sheet handle (never rendered at lg+). Tap toggles, a short
            swipe expands/collapses, and while collapsed the bar wears the
            aggregate agent-attention dot so a waiting draft / flag /
            determination shows through the closed sheet. */}
        <button
          type="button"
          onClick={onHandleClick}
          onTouchStart={onHandleTouchStart}
          onTouchMove={onHandleTouchMove}
          onTouchEnd={onHandleTouchEnd}
          aria-expanded={sheet}
          className="relative -mx-3 flex min-h-[44px] flex-col items-center justify-center gap-1 rounded-t-2xl px-3 pt-2 pb-1 lg:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
        >
          <span className="h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600" aria-hidden="true" />
          <span className="sr-only">{sheet ? 'Collapse lead details' : 'Expand lead details'}</span>
          {!sheet && agentTabAttention(localThread) && (
            <>
              <span className="absolute right-4 top-2.5 h-2 w-2 rounded-full bg-primary-500" aria-hidden="true" />
              <span className="sr-only">Agent needs attention</span>
            </>
          )}
          {!sheet && !(presence?.loaded && presence.hasConversation) && (
            <span className="flex items-center gap-1.5 pb-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
              Lead details
              <ChevronUp className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
            </span>
          )}
        </button>

        {/* 1 — the agent's pinned status line (portalled in by AgentPanel when a
            conversation exists). `empty:hidden` closes the flex gap on the leads
            that have no agent, so the tabs sit where they always did. In the
            collapsed sheet this IS the peek content: state · phase · Take over
            stay tappable without opening anything. */}
        <div ref={setSlotEl} className="empty:hidden" />

        {/* 2 — the tab strip. Hidden (never unmounted) while the sheet is
            collapsed; plain row otherwise — the sheet body scrolls under it at
            max-lg and the lg rail's panels scroll independently anyway. */}
        <div className={`flex gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-900/60 ${sheet ? '' : 'max-lg:hidden'}`}>
          {tabBtn('next', 'Next steps')}
          {agentTab && tabBtn('agent', 'Agent', agentTabAttention(localThread))}
          {tabBtn('screening', 'Screening', hasRunnableStudy)}
          {tabBtn('profile', profileLabel)}
        </div>

        {/* 3 — "randomized here, still in play there" (L4). Rail-level, not
            card-level: it used to live inside the Studies card, which put it on
            the Screening tab only — so a recruiter working Next steps could text,
            book or hand to the agent a lead who is already enrolled somewhere and
            never see it. Renders null when there is nothing to say, and follows
            the tab strip's collapsed-sheet rule (the peek stays a peek; opening
            the sheet shows it on whichever tab is up). */}
        <div className={`empty:hidden ${sheet ? '' : 'max-lg:hidden'}`}>
          <RandomizedConflictBanner memberships={detail.memberships} />
        </div>

        {/* 4 — the four panels. Each mounts on first visit and then stays
            mounted; only `hidden` moves after that (see `visited` above).
            In the expanded sheet this wrapper is the scroll container. */}
        <div className={`min-h-0 flex-1 ${sheet ? 'max-lg:overflow-y-auto max-lg:overscroll-contain' : 'max-lg:hidden'}`}>
        {/* Agent. Mounted even when its tab is hidden — it is the page's only
            source of AgentThreadState and of the presence above. */}
        <div
          className={`lg:h-full rounded-xl border border-gray-200 bg-white p-4 empty:hidden dark:border-gray-700 dark:bg-gray-800 lg:overflow-y-auto ${
            tab === 'agent' ? '' : 'hidden'
          }`}
        >
          <AgentPanel
            contactId={agentContactId}
            studyId={agentStudyId}
            onThreadState={onThreadStateWiretap}
            focusScreenKey={focusScreenKey}
            focusNonce={focusNonce}
            frameless
            statusLineSlot={slotEl}
            onPresence={setPresence}
          />
        </div>

        {/* Next steps — one card box, five rows, dividers between them; the
            rows own their padding (hence no p-4 here). */}
        <div
          className={`lg:h-full divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800 lg:overflow-y-auto ${
            tab === 'next' ? '' : 'hidden'
          }`}
        >
          {visited.has('next') && (
            <NextStepsTab
              detail={detail}
              reloadToken={reloadToken}
              pingTick={pingTick}
              onSent={onSent}
              prefillComposer={prefillComposer ? (text) => {
                // A drafted SMS lands in the page composer, which on a phone
                // sits behind the sheet — collapse so the recruiter sees it.
                setSheet(false);
                prefillComposer(text);
              } : undefined}
              scheduleReadiness={scheduleReadiness}
              onToggleGate={onToggleGate}
            />
          )}
        </div>

        {/* Screening — NO card box: the runner and the attempt history render
            their own cards, and a second one around them double-chromes it. */}
        <div className={`lg:h-full lg:overflow-y-auto ${tab === 'screening' ? '' : 'hidden'}`}>
          {visited.has('screening') && (
            <ScreeningTab
              contactId={c.id}
              contactStudySlugs={contactStudySlugs}
              reloadToken={reloadToken}
              onChanged={onChanged}
              memberships={detail.memberships}
            />
          )}
        </div>

        {/* Profile — frameless, so its nine read-only cards read as one grouped
            list inside this box instead of nine boxes inside a box. */}
        <div
          className={`lg:h-full rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 lg:overflow-y-auto ${
            tab === 'profile' ? '' : 'hidden'
          }`}
        >
          {visited.has('profile') && (
            <ProfileTab
              detail={detail}
              enrollments={enrollments}
              reloadToken={reloadToken}
              pingTick={pingTick}
              isSuperAdmin={isSuperAdmin}
              reenrolling={reenrolling}
              onReEnroll={onReEnroll}
              frameless
            />
          )}
        </div>
      </div>
      </div>
    </>
  );
}
